import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Space, Tag, Spin, Alert, Switch, Row, Col } from 'antd';
import CBInputNumber from '../../components/CBInputNumber';
import { SaveOutlined, ReloadOutlined, IdcardOutlined, CloudOutlined, BankOutlined } from '@ant-design/icons';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { avecLibelleTraduit } from '../../i18n/libelles';
// Liste des clés gérées par cette page. typeParametre + description = utilisés
// pour créer la ligne en base si elle n'existe pas encore.
// label et description : traduits à la lecture (parametres:attestations.libelles / .descriptions).
const CLES = avecLibelleTraduit(avecLibelleTraduit([
  {
    cle: 'PRIX_UNITAIRE_ATTESTATION_FCFA', type: 'MONTANT', suffix: 'FCFA',
    section: 'tarifs', required: true
  },
  {
    cle: 'CONTRIBUTION_FONDS_ATTESTATION_FCFA', type: 'MONTANT', suffix: 'FCFA',
    section: 'tarifs', required: true
  },
  {
    cle: 'PRIX_UNITAIRE_ATTESTATION_DIGITALE_FCFA', type: 'MONTANT', suffix: 'FCFA',
    section: 'tarifs-digitaux', required: true
  },
  {
    cle: 'APPLIQUER_TVA_ATTESTATION_DIGITALE', type: 'BOOLEEN', section: 'tarifs-digitaux'
  },
  {
    cle: 'TAUX_TVA_ATTESTATION_DIGITALE', type: 'TAUX', suffix: '%',
    section: 'tarifs-digitaux'
  },
  {
    cle: 'BNCB_RAISON_SOCIALE', type: 'TEXTE', section: 'bureau'
  },
  {
    cle: 'BNCB_VILLE', type: 'TEXTE', section: 'bureau'
  },
  {
    cle: 'BNCB_PAYS', type: 'TEXTE', section: 'bureau'
  },
  {
    cle: 'BNCB_SIGNATAIRE', type: 'TEXTE', section: 'bureau'
  },
  {
    cle: 'BNCB_BENEFICIAIRE_CHEQUE', type: 'TEXTE', section: 'bureau'
  },
  // Coordonnées affichées dans l'en-tête du PDF. Optionnelles : si vides,
  // le PDF utilise (s'il existe) l'organisme BUREAU_NATIONAL en base.
  {
    cle: 'BNCB_ADRESSE', type: 'TEXTE', section: 'bureau'
  },
  {
    cle: 'BNCB_BP', type: 'TEXTE', section: 'bureau'
  },
  {
    cle: 'BNCB_TELEPHONE', type: 'TEXTE', section: 'bureau'
  },
  {
    cle: 'BNCB_EMAIL', type: 'TEXTE', section: 'bureau'
  },
], 'parametres:attestations.libelles', { cle: 'cle' }), 'parametres:attestations.descriptions', { cle: 'cle', champ: 'description' });

export default function ParametresAttestations() {
  const { t } = useTranslation('parametres');
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Map cle -> { parametreTrackingId, valeur } - null si la clé n'existe pas en base.
  const [parametres, setParametres] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/v1/parametres');
      const all = res?.data?.data || [];
      const map = {};
      const initial = {};
      CLES.forEach(({ cle, type }) => {
        const found = all.find(p => p.cle === cle);
        map[cle] = found || null;
        if (!found) {
          initial[cle] = type === 'BOOLEEN' ? false : '';
        } else if (type === 'MONTANT' || type === 'TAUX') {
          initial[cle] = Number(found.valeur);
        } else if (type === 'BOOLEEN') {
          initial[cle] = found.valeur === 'true';
        } else {
          initial[cle] = found.valeur;
        }
      });
      setParametres(map);
      form.setFieldsValue(initial);
    } catch {
      toast.error(t('attestations.messages.erreurChargement'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onSave = async (vals) => {
    setSaving(true);
    try {
      // Sauvegarde séquentielle pour préserver l'ordre des messages d'erreur.
      const operations = [];
      for (const def of CLES) {
        let valeurStr;
        if (def.type === 'BOOLEEN') {
          valeurStr = vals[def.cle] ? 'true' : 'false';
        } else {
          valeurStr = vals[def.cle] == null || vals[def.cle] === ''
            ? null : String(vals[def.cle]).trim();
        }
        const existant = parametres[def.cle];

        if (!valeurStr && def.type !== 'BOOLEEN') continue; // on n'écrase pas avec une valeur vide
        if (existant && existant.valeur === valeurStr) continue; // pas de changement

        const payload = {
          typeParametre: def.type,
          cle: def.cle,
          valeur: valeurStr,
          // Valeur enregistree en base : toujours en francais, quelle que soit la langue de l'ecran.
          description: i18n.t(`parametres:attestations.descriptions.${def.cle}`, { lng: 'fr' }),
        };

        if (existant) {
          operations.push(axiosInstance.put(`/v1/parametres/${existant.parametreTrackingId}`, payload));
        } else {
          operations.push(axiosInstance.post('/v1/parametres', payload));
        }
      }
      if (operations.length === 0) {
        toast.info(t('attestations.messages.aucuneModification'));
        return;
      }
      await Promise.all(operations);
      toast.success(t('attestations.messages.enregistres', { count: operations.length }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('attestations.messages.erreurEnregistrement'));
    } finally {
      setSaving(false);
    }
  };

  const renderField = (def) => {
    const existant = parametres[def.cle];
    const isBooleen = def.type === 'BOOLEEN';
    return (
      <Col xs={24} md={def.full ? 24 : 12} key={def.cle}>
        <Form.Item
          name={def.cle}
          valuePropName={isBooleen ? 'checked' : 'value'}
          label={
            <Space size={6}>
              <span>{def.label}</span>
              {!existant && !isBooleen && (
                <Tag color="orange" style={{ fontSize: 10, margin: 0, borderRadius: 10 }}>{t('attestations.champs.nonConfigure')}</Tag>
              )}
            </Space>
          }
          extra={<span style={{ fontSize: 11.5, color: '#9ca3af' }}>{def.description}</span>}
          rules={def.required ? [{ required: true, message: t('attestations.champs.valeurRequise') }] : []}
        >
          {def.type === 'MONTANT' ? (
            <CBInputNumber style={{ width: '100%' }} min={0} step={50}
              addonAfter={def.suffix}
              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
              parser={(v) => v?.replace(/\s/g, '')} />
          ) : def.type === 'TAUX' ? (
            <CBInputNumber style={{ width: '100%' }} min={0} max={100} step={1}
              addonAfter="%" precision={2} />
          ) : isBooleen ? (
            <Switch checkedChildren={t('attestations.champs.oui')} unCheckedChildren={t('attestations.champs.non')} />
          ) : (
            <Input placeholder={def.description} />
          )}
        </Form.Item>
      </Col>
    );
  };

  const renderSection = ({ icon, color, bg, titre, sousTitre, section, alerte }) => (
    <div className="cb-card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10, background: bg, color,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0,
        }}>
          {icon}
        </div>
        <div>
          <div className="cb-card-title">{titre}</div>
          {sousTitre && <div style={{ fontSize: 12, color: '#6b7280' }}>{sousTitre}</div>}
        </div>
      </div>
      {alerte && (
        <Alert type="info" showIcon style={{ marginBottom: 16, borderRadius: 8 }} message={alerte} />
      )}
      <Row gutter={[16, 0]}>
        {CLES.filter((c) => c.section === section).map(renderField)}
      </Row>
    </div>
  );

  return (
    <Layout title={t('attestations.page.titre')}>
      <div>
        <div className="cb-page-header">
          <div>
            <h1 className="cb-page-title">{t('attestations.page.titre')}</h1>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load} style={{ borderRadius: 8 }}>
              {t('attestations.actions.recharger')}
            </Button>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => form.submit()}
              style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8 }}>
              {t('attestations.actions.enregistrer')}
            </Button>
          </Space>
        </div>

        {loading ? (
          <div style={{ padding: 80, textAlign: 'center' }}><Spin /></div>
        ) : (
          <Form form={form} layout="vertical" onFinish={onSave}>
            {renderSection({
              icon: <IdcardOutlined />, color: '#1a6b3c', bg: '#e8f5ed',
              titre: t('attestations.sections.tarifsTitre'),
              sousTitre: t('attestations.sections.tarifsSousTitre'),
              section: 'tarifs',
              alerte: t('attestations.sections.tarifsAlerte'),
            })}
            {renderSection({
              icon: <CloudOutlined />, color: '#0e7490', bg: '#e0f7fa',
              titre: t('attestations.sections.digitalesTitre'),
              sousTitre: t('attestations.sections.digitalesSousTitre'),
              section: 'tarifs-digitaux',
              alerte: t('attestations.sections.digitalesAlerte'),
            })}
            {renderSection({
              icon: <BankOutlined />, color: '#9b563a', bg: '#f5ede8',
              titre: t('attestations.sections.bureauTitre'),
              sousTitre: t('attestations.sections.bureauSousTitre'),
              section: 'bureau',
            })}
          </Form>
        )}
      </div>
    </Layout>
  );
}
